interface ISalesUnit {
    name: string;
    code: string;
    quantity: number;
    hide: boolean;
}
interface ISiteLabelCalculatorConfig
    extends ISiteLabelConfig {
    
    units: ISalesUnit[];
    minQuantity: number | null;
    maxQuantity: number | null;
}
interface ISiteLabelCalculatorHelper
{
    setSalesUnits() : void;
    setValue() : void;
}

class SiteLabelCalculator
    extends SiteLabel<number, ISiteLabelCalculatorConfig> {

    private readonly _dependencies: IFilterDepencency[] | null;
    private readonly _changedEvent?: (data: ISiteModelValueChanged) => void;
    private readonly _container: IQueryElement;
    private readonly _input: IQueryElement;
    private readonly _header: IQueryElement;
    private readonly _content: ISiteLabelCalculatorHelper;

    constructor(card: SiteCard, model: ISiteModel, modelData: ISiteModel, element: HTMLElement) {
        super(card, model, modelData, element);

        let input = this.query("input").first();
        let dependencies = input && input.getAttr("data-dependencies");

        if(input === null || dependencies === null)
            throw 'SiteLabelDate: Missing element(s)';

        this._input = input;
        this._input.on("input", () => {
            this.onValueChanged();
        });

        if(Site.isMobile() && !Site.hasKeyboard() && !card.SiteConfig.settings.includes("CalculatorUsePicker"))
            this._input.setAttr("readonly", "true");
        
        let icon = this.getIcon();
        icon && icon.on("click", () => { this.showToggle(); });

        let backspace = this.query("i.inline.backspace").first();
        // TODO: Use Press, blocking issue on iPhone
        backspace && backspace.on("click", (e) => { 
            this.setValue(null); 
            this.onValueChanged(); 
        })

        this._container = QueryElement.QueryElement('div')
            .addClass("calculator")
            .addClass("popout")
            .addClass("popout-" + this.config.popout);

        this._header = QueryElement.QueryElement('div')
            .addClass("header")
            .appendTo(this._container);
        
        if (card.SiteConfig.settings.includes("CalculatorUsePicker")) {
            this._content = new SiteLabelCalculatorPicker(this)
                .appendTo(this._container);
        } else {
            this._content = new SiteLabelCalculatorNumpad(this)
                .appendTo(this._container);
        }


        this._dependencies = dependencies ? QueryJSON.deserialize<Array<IFilterDepencency>>(dependencies) : null;
        if (this._dependencies && this._dependencies.length > 0) {
            // Subscribe for model changes
            this._changedEvent = this.getCard().Model.changedEvent.on(data => {
                // Get first item of path not equalling current path (so we make relative paths form an absolute one)
                for (var x = 0; x < Math.min(data.path.length, this.path.length); x++) {
                    if (this.path[x] != data.path[x])
                        break;
                }
                // And now join it
                let property = data.path.slice(x).join(".");

                if (this._dependencies
                    && this._dependencies.some(a => a.property == property)
                    && data.changer instanceof SiteLabelUnit) {
                    
                    this.config.units = [];

                    // Order it from big to small
                    data.value.reverse();
                    for (let value of data.value)
                    {
                        let name = data.changer.getModelData()[QueryJSON.toCamelCase(value.salesUnit)];

                        this.config.units.push({
                            quantity: value.quantity,
                            name: name,
                            code: name[0],
                            hide: false
                        });
                    }

                }
            });
        }

        this.append(this._container);
    }
    getInput() {
        return this._input;
    }
    preventContinue(e: Event)
    {
        if (this.config.popout != "always")
            e.stop();    
    }
    defaultConfig(): ISiteLabelCalculatorConfig {
        return {
            popout: "normal",
            units: [],
            minQuantity: null,
            maxQuantity: null,
            scroll: "top"
        }
    }
    show() {
        this._container.addClass("show");
    }
    unShow() {
        this._container.removeClass("show");
    }
    showToggle() {
        if (this._container.containsClass("show"))
            this.unShow();
        else
            this.show();
    }
    defaultKeyDownHandler(e: KeyboardEvent) {
        let addKey = "";
        switch (e.keyCode) {
            case 117: // F6
                addKey = "F"
                break;
            case 118: // F7
                addKey = "B"
                break;    
            case 119: // F8  
                addKey = "S"
                break;
            case 38: // KEY UP 
                this.moveUp();
                break;
            case 40: // KEY DOWN 
                this.moveDown();    
                break;   
        }
        if (addKey) {
            this._input.setVal((this._input.getVal() || "") + addKey + " ");
            this.preventContinue(e);
        }

        if (e.keyCode != 9)
            this.show();
        super.defaultKeyDownHandler(e);
    }
    moveUp() {
        var unit = this.getSmallestUnit();
        var newValue = (this.getValue() || 0) + unit;

        if (this.config.maxQuantity != null && newValue > this.config.maxQuantity)
            this.setValue(this.config.maxQuantity);
        else
            this.setValue(newValue);
        
        this.onValueChanged();
    }
    moveDown() {
        var value = this.getValue() || 0;
        if (value == 0) {
            if (this.config.maxQuantity != null)
                this.setValue(this.config.maxQuantity);
        } else {
            var unit = this.getSmallestUnit();
            var newValue = (this.getValue() || 0) - unit;

            if (newValue < 0)
                this.setValue(0);
            else
                this.setValue(newValue);
        }
        
        this.onValueChanged();
    }
    makeActive() {
        if(super.makeActive()) {
            this.renderHeader();
            this._content.setSalesUnits();

            if (!this.getSiteConfig().settings.includes("NoAutoPopout"))
                this.show();
            
            this._input.select();

            return true;
        }
        return false;
    }
    makeInactive() {
        super.makeInactive();
        this.unShow();
    }
    renderHeader() {
        let quantity = this.getValue() || 0;
        let text = this.numberToString(quantity, true).join(", ");

        this._header
            .clear()
            .append(QueryElement.QueryElement('div')
                .addClass("text")
                .setHtml(text))
            .append(QueryElement.QueryElement('div')
                .addClass("numeric")
                .setHtml(`${quantity}x`))
    }
    getValue() {
        return this.stringToNumber(this._input.getVal() || "") || null;
    }
    setValue(value: number | null) {
        let text = this.numberToString(value || 0, false);

        super.setValue(value);
        this._input.setVal(text.join(" ") + " ");
    }
    onValueChanged() {
        this.renderHeader();
        this._content.setValue();

        super.setValue(this.getValue());
    }
    numberToString (number: number, fullName: boolean = false) {
        let bestUnits = Math.getBest(
            new MathBest(
                number,
                this.getQuantities()
            )
        );

        let str = [];
        if (bestUnits.rest != 0)
            str.push(bestUnits.rest.toString());
        
        for(let x in bestUnits.output) {
            let number = bestUnits.output[x];
            if (number == 0)
                continue;
            
            let unit = this.getUnitFromQuantity(Number.parseInt(x));
            str.push(fullName ? `${number} ${unit.name}` : `${number}${unit.code}`);
        }

        return str.reverse();
    }
    stringToNumber (string: string) {
        let total = 0;
        let re = /(([\-0-9]*)[\s]*([a-z\/\*\+\.]*))/gi;

        let m: RegExpExecArray | null;
        while ((m = re.exec(string)) !== null) {
            if (m.index === re.lastIndex) {
                re.lastIndex++;
            }
            
            if (m[2] || m[3]) {
                let unit = this.getUnitFromCode(m[3]);
                total += unit.quantity * Number.parseInt(!m[2] ? "1" : m[2]);
            }
        }
        return total;
    }
    getQuantities() {
        return this.getUnits()
                    .map(a => a.quantity)
                    .sort((a, b) => b - a)
    }
    getUnits() {
        // Copy the array:
        var units = (this.config.units || []).slice(0);

        // Add quantity 1:
        if (!units.some(a => a.quantity == 1))
            units.push({ code: "x", name: "x", quantity: 1, hide: false });
        
        return units.filter(a => a.quantity > 0);
    }
    private getSmallestUnit() {
        var units = this.getUnits()
            .map(a => a.quantity)
            .filter(a => a > 1)
            .sort((a, b) => a - b);
        
        if (units.length == 0)
            return 1;
        else
            return units[0];
    }
    private getUnitFromCode(unit: string) {
        return this.getUnits().filter(a => a.code.toLowerCase() == unit.toLowerCase())[0] || {
            code: "",
            quantity: 1
        };
    }
    private getUnitFromQuantity(quantity: number) {
        return this.getUnits().filter(a => a.quantity == quantity)[0] || {
            code: "",
            quantity: 1
        };
    }

    async remove(removeInner = true) {
        await super.remove(removeInner);

        if(this._changedEvent)
            this.getCard().Model.changedEvent.off(this._changedEvent);
    }
} 
class SiteLabelCalculatorPicker
    extends QueryElement<HTMLElement>
    implements ISiteLabelCalculatorHelper {

    private readonly _calculator: SiteLabelCalculator;
    private readonly _maxNumbers: number = 6;
    private _picker: IQueryElement; 
    private _buttons: IQueryElement; 

    constructor(calculator: SiteLabelCalculator) {
        super(document.createElement("div"), true);

        this._calculator = calculator;
        this._picker = QueryElement.QueryElement("div").addClass("picker");
        this._buttons = QueryElement.QueryElement("div").addClass("buttons");

        if(this._calculator.config.maxQuantity != null) {
            let button = new SiteButton({
                title: "Rest<b>x" + this._calculator.config.maxQuantity + "</b>"
            });
            button.on("click", (e) => {
                this._calculator.setValue(this._calculator.config.maxQuantity);
                this._calculator.onValueChanged();
                this._calculator.getInput().select();
                this._calculator.makeNextActive();

                this._calculator.preventContinue(e);
                this.setValue();
            });
            this._buttons.append(button);
        }

        let button = new SiteButton({
            title: "OK"
        });
        button.on("click", (e) => {
            this._calculator.makeNextActive(true); 
            e.stop();
        });
        this._buttons.append(button);

        this.addClass("content");
        this.append(this._buttons);
    }

    setValue() {
        this.setSalesUnits();
    }
    setSalesUnits() {
        this._picker.remove();
        this._picker = QueryElement.QueryElement("div").addClass("picker");
        
        let bestUnits = Math.getBest(
            new MathBest(
                this._calculator.getValue() || 0,
                this._calculator.getQuantities()
            )
        );

        let prevQuantity = 0;
        var units = this._calculator.getUnits()
            .filter(a => a.quantity > 1)
            .sort((a, b) => a.quantity - b.quantity)
            .reverse();
        
        for(let item of units) {
            let maxNumber = 0;
            if (prevQuantity)
                maxNumber = (Math.lcm(item.quantity, prevQuantity) / item.quantity) - 1;
            this.renderUnit(item, bestUnits.output[item.quantity] || 0, maxNumber);
            
            prevQuantity = item.quantity;
        }

        this.append(this._picker);
    }
    renderUnit(unit: ISalesUnit, value: number, maxNumber: number) {
        let config = this._calculator.config;

        let hasMax = true;
        let minNumber = 0;
        if (config.minQuantity != null)
            minNumber = config.minQuantity / unit.quantity;
        
        if (maxNumber == 0 || 
            (
                config.maxQuantity != null &&
                maxNumber * unit.quantity > config.maxQuantity
            )
        ) {
            hasMax = config.maxQuantity != null;
            maxNumber = 0;
            if (config.maxQuantity != null)
                maxNumber = config.maxQuantity / unit.quantity;
        }

        let container = QueryElement.QueryElement("div").addClass("unit");
        QueryElement.QueryElement("div")
            .addClass("header")
            .setHtml(`<span>${unit.name}</span>${unit.quantity}x`)
            .appendTo(container);
        
        let startNumber = Math.round(value - ((this._maxNumbers - 1) / 2));
        if (startNumber < minNumber)
            startNumber = minNumber;
        
        if (startNumber < 1)
            startNumber = 1;
        
        let secondMaxNumber = startNumber + this._maxNumbers - 1;

        if (!hasMax || maxNumber > secondMaxNumber)
            maxNumber = secondMaxNumber;

        this.renderButton(0, value, unit.quantity).appendTo(container);
        for(var x = startNumber; x <= maxNumber; x++)
        {
            this.renderButton(x, value, unit.quantity).appendTo(container);
        }
        
        this._picker.append(container);
    }
    renderButton(value: number, selectedValue: number, quantity: number)
    {
        let button = new SiteButton({
            title: value.toString()
        });
        
        if (value == selectedValue)
            button.addClass("selected");
        
        button.on("click", (e) => {
            let input = this._calculator.getInput();
            let currValue = this._calculator.getValue() || 0;
            input.setVal(`${currValue - (selectedValue * quantity) + (value * quantity)}`);
            this._calculator.onValueChanged();
            input.select();
            
            this._calculator.makeNextActive();
            this._calculator.preventContinue(e);
        });

        return button;
    }
}

class SiteLabelCalculatorNumpad
    extends QueryElement<HTMLElement>
    implements ISiteLabelCalculatorHelper {
    
    private readonly _calculator: SiteLabelCalculator;
    private _buttons: IQueryElement | null = null;

    constructor(calculator: SiteLabelCalculator) {
        super(document.createElement("div"), true);

        this._calculator = calculator;
        
        this.addClass("content");
        this.drawNumbers();
    }
    drawNumbers() {
        let numbers = QueryElement.QueryElement('div')
            .addClass("numbers")
            .appendTo(this);
        
        let numbersToDraw = ["7","8","9","4","5","6","1","2","3","OK","0"];
        for (let title of numbersToDraw) {
            let button = new SiteButton({
                title: title
            });
            if(title == "OK")
                button.on("click", (e) => { 
                    this._calculator.makeNextActive(true); 
                    e.stop();
                });
            else
                button.on("click", (e) => {
                    let input = this._calculator.getInput();
                    input.setVal(input.getVal() + title);
                    this._calculator.onValueChanged();

                    this._calculator.preventContinue(e);
                });
            numbers.append(button);
        }

        let button = new SiteButton({
            title: "-/-"
        });
        button.on("click", (e) => {
            let input = this._calculator.getInput();
            input.setVal(input.getVal() + "-");
            this._calculator.onValueChanged();

            this._calculator.preventContinue(e);
        });
        numbers.append(button);
    }
    setSalesUnits() {
        if(this._buttons !== null)
            this._buttons.remove();
        
        this._buttons = QueryElement.QueryElement('div')
            .addClass("buttons");
        
        for(let item of this._calculator.getUnits()) {
            if (item.quantity <= 0)
                continue;

            let button = new SiteButton({
                title: item.name + "<b>x" + item.quantity + "</b>"
            });
            button.on("click", (e) => {
                var input = this._calculator.getInput();
                input.setVal((input.getVal() || "") + item.code + " ");
                this._calculator.onValueChanged();
                input.select();

                this._calculator.makeNextActive();
                this._calculator.preventContinue(e);
            });
            this._buttons.append(button);
        }
        var config = this._calculator.config
        if(this._calculator.config.maxQuantity != null) {
            let button = new SiteButton({
                title: "Rest<b>x" + this._calculator.config.maxQuantity + "</b>"
            });
            button.on("click", (e) => {
                this._calculator.setValue(this._calculator.config.maxQuantity); 
                this._calculator.onValueChanged();
                this._calculator.getInput().select();

                this._calculator.makeNextActive();
                this._calculator.preventContinue(e);
            });
            this._buttons.append(button);
        }

        this.append(this._buttons);
    }
    setValue() {

    }
}
